import React from 'react';
import { X, Menu, MessageSquare, BookOpen, Sparkles, Lock, AlertCircle, Mic, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { ChatMode } from '@/types/chat';
import { usePrayerChoice } from '@/contexts/PrayerChoiceContext';
import { usePrayerCompanionAccess } from '@/hooks/usePrayerCompanionAccess';
import { usePrayerCompanionUsage } from '@/hooks/usePrayerCompanionUsage';
import type { PrayerCompanionMode } from '@/types/prayerCompanion.types';
import { LAYERS } from '@/utils/layers';
import { CompanionSettingsDialog } from './CompanionSettingsDialog';

interface CompanionHeaderProps {
  onClose: () => void;
  onOpenAiAssistant: () => void;
  onModeChange: (mode: ChatMode) => void;
  currentMode: ChatMode;
}

export function CompanionHeader({ 
  onClose, 
  onOpenAiAssistant, 
  onModeChange,
  currentMode
}: CompanionHeaderProps) {
  const { companionMode, setCompanionMode } = usePrayerChoice();
  const { 
    canAccessMode, 
    canInteractWithMode, 
    getAccessDeniedReason,
    getRemainingInteractions,
    hasUnlimitedInteractions
  } = usePrayerCompanionAccess();
  const { incrementUsage } = usePrayerCompanionUsage();
  const { toast } = useToast();

  const handlePrayerModeClick = async () => {
    if (!canInteractWithMode('prayer')) {
      const reason = getAccessDeniedReason('prayer');
      
      if (reason === 'NO_ACCESS') {
        toast({
          title: "Premium feature",
          description: "Upgrade to premium to unlock guided prayer mode and enjoy unlimited access.",
          duration: 4000,
        });
      } else if (reason === 'DAILY_LIMIT') {
        toast({
          title: "Daily limit reached",
          description: "You've reached your daily limit for prayer mode. Upgrade to premium for unlimited access or try again tomorrow.",
          duration: 4000,
        });
      }
      return;
    }

    const success = await incrementUsage('prayer');
    
    if (success) {
      setCompanionMode('prayer');
    }
  };

  const handleRegularChatClick = async () => {
    if (!canInteractWithMode('cards')) {
      const reason = getAccessDeniedReason('cards');
      if (reason === 'NO_ACCESS') {
        toast({
          title: "Sign in required",
          description: "Please sign in to access this feature. It's free to get started!",
          duration: 4000,
        });
      } else if (reason === 'DAILY_LIMIT') {
        toast({
          title: "Daily limit reached",
          description: "You've reached your daily limit for cards mode. Upgrade to premium for unlimited access or try again tomorrow.",
          duration: 4000,
        });
      }
      return;
    }

    const success = await incrementUsage('cards');
    if (success) {
      setCompanionMode('cards');
      onModeChange('regular');
    }
  };

  const handleStudyModeClick = async () => {
    if (!canInteractWithMode('chat')) {
      const reason = getAccessDeniedReason('chat');
      if (reason === 'NO_ACCESS') {
        toast({
          title: "Premium feature",
          description: "Upgrade to premium to unlock chat mode and enjoy unlimited access.",
          duration: 4000,
        });
      } else if (reason === 'DAILY_LIMIT') {
        toast({
          title: "Daily limit reached",
          description: "You've reached your daily limit for chat mode. Upgrade to premium for unlimited access or try again tomorrow.",
          duration: 4000,
        });
      }
      return;
    }

    const success = await incrementUsage('chat');
    if (success) {
      setCompanionMode('chat');
      onModeChange('study');
    }
  };

  const handleRealtimeModeClick = async () => {
    if (!canInteractWithMode('realtime')) {
      const reason = getAccessDeniedReason('realtime');
      
      if (reason === 'NO_ACCESS') {
        toast({
          title: "Premium feature",
          description: "Upgrade to premium to unlock live prayer mode and enjoy unlimited access.",
          duration: 4000,
        });
      } else if (reason === 'DAILY_LIMIT') {
        toast({
          title: "Daily limit reached",
          description: "You've reached your daily limit for live prayer mode. Upgrade to premium for unlimited access or try again tomorrow.",
          duration: 4000,
        });
      }
      return;
    }

    const success = await incrementUsage('realtime');
    
    if (success) {
      setCompanionMode('realtime');
    }
  };

  // Helper function to get access status for a mode
  const getAccessStatus = (mode: PrayerCompanionMode) => {
    if (!canAccessMode(mode)) {
      return { 
        accessible: false, 
        reason: 'NO_ACCESS',
        icon: <Lock className="h-3 w-3" />,
        badge: 'Upgrade Required'
      };
    }
    
    if (!canInteractWithMode(mode)) {
      const remaining = getRemainingInteractions(mode);
      return {
        accessible: false,
        reason: 'DAILY_LIMIT',
        icon: <AlertCircle className="h-3 w-3" />,
        badge: 'Limit Reached'
      };
    }

    const remaining = getRemainingInteractions(mode);
    return {
      accessible: true,
      reason: 'ACCESSIBLE',
      remaining: hasUnlimitedInteractions ? 'Unlimited' : remaining
    };
  };

  // ... imports and code above remain unchanged ...

  // Enhanced header content with subtitle
  const getHeaderContent = () => {
    if (companionMode === 'prayer') {
      return {
        icon: <Sparkles className="h-4 w-4 text-gold" />,
        title: 'Prayer Guidance with Abiel',
        subtitle: 'Personalized guidance for your prayers'
      };
    } else if (companionMode === 'chat') {
      return {
        icon: <BookOpen className="h-4 w-4 text-primary" />,
        title: 'Bible Study with Abiel',
        subtitle: 'Deep dive into Scripture'
      };
    } else if (companionMode === 'realtime') {
      return {
        icon: <Mic className="h-4 w-4 text-blue-500" />,
        title: 'Speech to Speech',
        subtitle: 'Voice conversation with Abiel'
      };
    } else {
      return {
        icon: <MessageSquare className="h-4 w-4 text-gold" />,
        title: 'Chat with Abiel',
        subtitle: 'Your AI spiritual companion'
      };
    }
  };

// ... rest of the file unchanged ...

  const headerContent = getHeaderContent();

  return (
    <div className="flex items-center justify-between p-4 bg-gradient-to-r from-background via-background/95 to-background/90 border-b border-border/50 backdrop-blur-sm sa-top sa-x">
      {/* Left side - Menu */}
      <div className="flex items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-9 w-9 p-0 hover:bg-accent/50 transition-all duration-200 hover:scale-105"
            >
              <Menu className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 bg-background/95 backdrop-blur-sm border-border/50" style={{ zIndex: LAYERS.DROPDOWN }}>
            {/* Prayer Mode */}
            <DropdownMenuItem 
              onClick={handlePrayerModeClick}
              disabled={!canInteractWithMode('prayer')}
              className={`gap-3 p-3 cursor-pointer hover:bg-accent/50 transition-colors ${
                companionMode === 'prayer' ? 'bg-accent/30' : ''
              } ${!canInteractWithMode('prayer') ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-gold/20 to-gold/10">
                <Sparkles className="h-4 w-4 text-gold" />
              </div>
              <div className="flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Ask Abiel For Prayer Guidance</span>
                  {(() => {
                    const status = getAccessStatus('prayer');
                    return !status.accessible ? (
                      <Badge variant="secondary" className="text-xs">
                        {status.icon}
                        {status.badge}
                      </Badge>
                    ) : status.remaining !== 'Unlimited' ? (
                      <Badge variant="outline" className="text-xs">
                        {status.remaining} left
                      </Badge>
                    ) : null;
                  })()}
                </div>
                <span className="text-xs text-muted-foreground">Get personalized prayer assistance</span>
              </div>
            </DropdownMenuItem>
            
            {/* Regular Chat Mode */}
            <DropdownMenuItem 
              onClick={handleRegularChatClick}
              disabled={!canInteractWithMode('cards')}
              className={`gap-3 p-3 cursor-pointer hover:bg-accent/50 transition-colors ${
                companionMode === 'cards' ? 'bg-accent/30' : ''
              } ${!canInteractWithMode('cards') ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-gold/20 to-gold/10">
                <MessageSquare className="h-4 w-4 text-gold" />
              </div>
              <div className="flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Regular Chat</span>
                  {(() => {
                    const status = getAccessStatus('cards');
                    return !status.accessible ? (
                      <Badge variant="secondary" className="text-xs">
                        {status.icon}
                        {status.badge}
                      </Badge>
                    ) : status.remaining !== 'Unlimited' ? (
                      <Badge variant="outline" className="text-xs">
                        {status.remaining} left
                      </Badge>
                    ) : null;
                  })()}
                </div>
                <span className="text-xs text-muted-foreground">General prayer and spiritual guidance</span>
              </div>
            </DropdownMenuItem>
            
            {/* Bible Study Mode */}
            <DropdownMenuItem 
              onClick={handleStudyModeClick}
              disabled={!canInteractWithMode('chat')}
              className={`gap-3 p-3 cursor-pointer hover:bg-accent/50 transition-colors ${
                companionMode === 'chat' ? 'bg-accent/30' : ''
              } ${!canInteractWithMode('chat') ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-primary/20 to-primary/10">
                <BookOpen className="h-4 w-4 text-primary" />
              </div>
              <div className="flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Bible Study Mode</span>
                  {(() => {
                    const status = getAccessStatus('chat');
                    return !status.accessible ? (
                      <Badge variant="secondary" className="text-xs">
                        {status.icon}
                        {status.badge}
                      </Badge>
                    ) : status.remaining !== 'Unlimited' ? (
                      <Badge variant="outline" className="text-xs">
                        {status.remaining} left
                      </Badge>
                    ) : null;
                  })()}
                </div>
                <span className="text-xs text-muted-foreground">Deep scripture study and commentary</span>
              </div>
            </DropdownMenuItem>
            
            {/* Live Prayer Mode - Only show if feature is enabled */}
            {import.meta.env.VITE_ABIEL_REALTIME_ENABLED === 'true' && (
              <DropdownMenuItem 
                onClick={handleRealtimeModeClick}
                disabled={!canInteractWithMode('realtime')}
                className={`gap-3 p-3 cursor-pointer hover:bg-accent/50 transition-colors ${
                  companionMode === 'realtime' ? 'bg-accent/30' : ''
                } ${!canInteractWithMode('realtime') ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-blue-500/20 to-blue-500/10">
                  <Mic className="h-4 w-4 text-blue-500" />
                </div>
                <div className="flex flex-col flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Companion-LiveTime</span>
                    {(() => {
                      const status = getAccessStatus('realtime');
                      return !status.accessible ? (
                        <Badge variant="secondary" className="text-xs">
                          {status.icon}
                          {status.badge}
                        </Badge>
                      ) : status.remaining !== 'Unlimited' ? (
                        <Badge variant="outline" className="text-xs">
                          {status.remaining} left
                        </Badge>
                      ) : null;
                    })()}
                  </div>
                  <span className="text-xs text-muted-foreground">Live speech-to-speech conversation</span>
                </div>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Center - Avatar, title, and subtitle */}
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-gold/20 to-gold/10 flex items-center justify-center">
          <img 
            src="/lovable-uploads/802777a6-02df-4a79-bd82-e72572ae69bc.png" 
            alt="Abiel Avatar" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="text-center">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-gold/20 to-gold/10">
              {headerContent.icon}
            </div>
            <h1 className="text-lg font-semibold bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
              {headerContent.title}
            </h1>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {headerContent.subtitle}
          </p>
        </div>
      </div>

      {/* Right side - Settings and Close buttons */}
      <div className="flex items-center gap-1">
        <CompanionSettingsDialog />
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="h-9 w-9 p-0 hover:bg-destructive/10 hover:text-destructive transition-all duration-200 hover:scale-105"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
